import { LinkProps, PopoverRootProps, Portal, Popover } from '@chakra-ui/react';
import { FC, useMemo } from 'react';
import { FiExternalLink } from 'react-icons/fi';
import { MdInfoOutline } from 'react-icons/md';
import { MBox, MDivider, MFlex, MIcon, MLink, MText, MVStack } from '.';

export const TEMPLATE_EXPRESSION_DOCS_URL =
  'https://docs.monetizenow.io/docs/conditional-terms';

export type TemplateExpressionDocumentType = 'quote' | 'invoice';

/**
 * Real field paths per document type. Terms on an invoice resolve against an
 * `invoice` record with `invoiceItems`, so reusing the quote examples there
 * would teach paths that render blank.
 */
const DOCUMENT_EXAMPLES: Record<
  TemplateExpressionDocumentType,
  { root: string; list: string; filterField: string; filterValue: string }
> = {
  quote: {
    root: 'quote',
    list: 'quoteOfferings',
    filterField: 'offeringId',
    filterValue: 'offr_1',
  },
  invoice: {
    root: 'invoice',
    list: 'invoiceItems',
    filterField: 'productId',
    filterValue: 'prod_1',
  },
};

type SyntaxEntry = {
  /** Literal syntax rendered as code; `{root}` / `{list}` are substituted */
  syntax: string;
  description: string;
};

type SyntaxSection = {
  title: string;
  entries: readonly SyntaxEntry[];
};

/**
 * Reference content for the help popover. Exported so a test can assert every
 * function the parser actually accepts is documented here — otherwise this
 * silently goes stale the next time someone adds a formula function.
 *
 * Kept at module scope so it is not rebuilt on every render.
 */
export const TEMPLATE_EXPRESSION_SYNTAX_SECTIONS: readonly SyntaxSection[] = [
  {
    title: 'Merge fields',
    entries: [
      {
        syntax: '{{{root}.amount}}',
        description: 'Insert a field value from this document.',
      },
      {
        syntax:
          '{{{root}.{list}[{filterField}="{filterValue}" @first].amount}}',
        description:
          'Filter a list to pick one item. @first or @last says which match to take.',
      },
      {
        syntax: '[{filterField}="{filterValue}", quantity>1, @and]',
        description:
          'Combine filters with @and or @or. Supports = != > >= < <=.',
      },
    ],
  },
  {
    title: 'Formatting',
    entries: [
      { syntax: '@currency', description: 'Format as money, e.g. $1,200.00.' },
      {
        syntax: '@date',
        description: 'Format as a short date, e.g. Dec 1, 2024.',
      },
      { syntax: '@dateTime', description: 'Format as a date and time.' },
      {
        syntax: '@number',
        description: 'Format as a plain number with separators.',
      },
      { syntax: '@percent', description: 'Format as a percentage.' },
      {
        syntax: '@number maximumFractionDigits=2',
        description:
          'Control decimal places. minimumFractionDigits also works.',
      },
    ],
  },
  {
    title: 'Formulas',
    entries: [
      {
        syntax: '{{FORMULA({root}.amount / 12) @currency}}',
        description: 'Do math on field values. Formats apply to the result.',
      },
      {
        syntax: '+ - * / % **',
        description: 'Arithmetic, with ( ) for grouping.',
      },
      {
        syntax: '{root}.amount > 1000 ? {root}.amount * 0.9 : {root}.amount',
        description: 'Comparisons, && || !, and if/else via a ? b : c.',
      },
    ],
  },
  {
    title: 'Totals across a list',
    entries: [
      {
        syntax: "SUM('{root}.{list}[].amount')",
        description: 'Add up every value in a list.',
      },
      { syntax: 'AVG(...)', description: 'Average of every value in a list.' },
      { syntax: 'COUNT(...)', description: 'How many items the list has.' },
      { syntax: 'MIN(...)', description: 'Smallest value in a list.' },
      { syntax: 'MAX(...)', description: 'Largest value in a list.' },
      {
        syntax: "FIELD('{root}.{list}[@first].amount')",
        description:
          'Read one field path inside a formula. Use single quotes so filter values keep their double quotes.',
      },
    ],
  },
  {
    title: 'Math functions',
    entries: [
      {
        syntax: 'round(value, decimals)',
        description: 'Round to the given decimals.',
      },
      { syntax: 'abs(value)', description: 'Absolute value.' },
      { syntax: 'floor(value)', description: 'Round down to a whole number.' },
      { syntax: 'ceil(value)', description: 'Round up to a whole number.' },
      { syntax: 'min(a, b)', description: 'Smaller of the given values.' },
      { syntax: 'max(a, b)', description: 'Larger of the given values.' },
      {
        syntax: 'clamp(value, lower, upper)',
        description: 'Keep a value within a range.',
      },
      {
        syntax: 'coalesce(a, b)',
        description: 'First value that is not blank.',
      },
      { syntax: 'sqrt(value)', description: 'Square root.' },
      { syntax: 'pow(base, exponent)', description: 'Raise to a power.' },
      { syntax: 'ln(value)', description: 'Natural logarithm.' },
      { syntax: 'log10(value)', description: 'Base-10 logarithm.' },
    ],
  },
];

/** Substitute the document-specific field names into an example */
export const applyDocumentExamples = (
  syntax: string,
  documentType: TemplateExpressionDocumentType,
): string => {
  const example = DOCUMENT_EXAMPLES[documentType];
  return syntax
    .replaceAll('{root}', example.root)
    .replaceAll('{list}', example.list)
    .replaceAll('{filterField}', example.filterField)
    .replaceAll('{filterValue}', example.filterValue);
};

interface TemplateExpressionHelpProps {
  /** Which record the terms resolve against, so examples show real paths */
  documentType?: TemplateExpressionDocumentType;
  /**
   * Stable QA hook. Every instance shares the same visible text, so screens
   * with more than one editor (terms + memo) or a repeated one (`.map()` over
   * conditional terms / legal entities) must pass a distinguishing id.
   */
  testId?: string;
  popoverProps?: Omit<PopoverRootProps, 'children'>;
  triggerProps?: Omit<LinkProps, 'children'>;
}

/**
 * Always-visible entry point to the merge-field and formula syntax reference,
 * shown under rich text editors that support `{{...}}` expressions. Paired
 * with `TemplateExpressionWarning`, which only appears once something is
 * already wrong — this is the affordance for finding the feature at all.
 */
export const TemplateExpressionHelp: FC<TemplateExpressionHelpProps> = ({
  documentType = 'quote',
  testId = 'template-expression-help-btn',
  popoverProps,
  triggerProps,
}) => {
  const sections = useMemo(
    () =>
      TEMPLATE_EXPRESSION_SYNTAX_SECTIONS.map(({ title, entries }) => ({
        title,
        entries: entries.map(({ syntax, description }) => ({
          syntax: applyDocumentExamples(syntax, documentType),
          description,
        })),
      })),
    [documentType],
  );

  return (
    <Popover.Root lazyMount unmountOnExit size="sm" {...popoverProps}>
      <Popover.Trigger asChild>
        <MLink
          as="button"
          type="button"
          display="inline-flex"
          alignItems="center"
          fontSize="sm"
          data-testid={testId}
          {...triggerProps}
        >
          <MIcon as={MdInfoOutline} mr="1" />
          Merge fields &amp; formulas
        </MLink>
      </Popover.Trigger>

      {/*
       * Portaled because every surface renders this inside a clipping
       * container — accordion panels for quote and invoice terms, and a modal
       * for Edit Invoice — where the popover would otherwise be cut off or
       * stack underneath.
       */}
      <Portal>
        <Popover.Positioner>
          <Popover.Content w="md" maxW="90vw">
            <Popover.Body maxH="60vh" overflowY="auto">
              <MFlex>
                <MLink
                  href={TEMPLATE_EXPRESSION_DOCS_URL}
                  as="a"
                  target="_blank"
                  rel="noopener noreferrer"
                  fontSize="sm"
                >
                  View documentation
                  <MIcon as={FiExternalLink} ml="1" color="tIndigo.base" />
                </MLink>
              </MFlex>
              <MDivider my="3" />
              <MText fontSize="sm" mb="2">
                Wrap a field in <MText as="code">{'{{ }}'}</MText> to drop its
                value into the text when the document is generated. Functions
                like <MText as="code">SUM</MText> and{' '}
                <MText as="code">round</MText> below only work inside{' '}
                <MText as="code">FORMULA(...)</MText>.
              </MText>

              <MVStack align="stretch" gap="3">
                {sections.map(({ title, entries }, sectionIndex) => (
                  <MBox key={title}>
                    {sectionIndex > 0 && <MDivider mb="3" />}
                    <MText
                      fontSize="xs"
                      fontWeight={600}
                      textTransform="uppercase"
                    >
                      {title}
                    </MText>
                    {entries.map(({ syntax, description }) => (
                      <MBox key={syntax} mt="2">
                        <MText
                          as="code"
                          display="block"
                          fontSize="xs"
                          fontFamily="mono"
                          wordBreak="break-all"
                        >
                          {syntax}
                        </MText>
                        <MText fontSize="xs">{description}</MText>
                      </MBox>
                    ))}
                  </MBox>
                ))}
              </MVStack>
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
};
